import {
  IFacilitySectionProps,
  IHousingSectionProps,
  ILTRestrictionSectionProps,
  IMealSectionProps,
  IOrderReportDetails,
  IPayBonusSectionProps,
  IPointOfIntrestSectionProps,
  IReportSectionProps,
  IRfoSectionProps,
  ITravelSectionProps,
  IUnitSectionProps,
} from 'app/models/Orders/OrderDetails';
import { missingField } from 'app/constants';
import moment from 'moment';

const formatTime = time => {
  time = time?.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];
  let convertedTime = time;
  if (time[0].split(' ').length > 1 && time[0].split(' ')[1].split(':').length > 1) {
    time = time[0].split(' ')[1].split(':');
    time[2] = +time[0] < 12 ? ' AM' : ' PM';
    time[0] = +time[0] % 12 || 12;
    convertedTime = time[0] + ':' + time[1] + time[2];
  }
  return convertedTime;
};

const formatNumberWithCommas = (amount: string) => {
  return (
    '$' +
    Number(amount)
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  );
};

const formatAmount = (amount: string) => {
  return (
    '$' +
    Number(amount)
      .toFixed(3)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  );
};
export const flattenOrderInfoDetails = (response: IOrderReportDetails) => {
  return {
    orderskillset: response?.orderSkillset || missingField,
    orderShifts: response?.orderShifts || missingField,
    numberOfWeeks: response?.numberOfWeeks || missingField,
    numberOfRequiredReferences: response?.numberOfRequiredReferences || missingField,
    numberOfPositions: response?.numberOfPositions || missingField,
    yearsOfExperience: response?.yearsOfExperience || missingField,
    maxFilesSent: response?.maxFilesSent
      ? response?.maxFilesSent.toString().toLowerCase() == 'true'
        ? 'Yes'
        : 'No'
      : missingField,
    numberOfFilesWFCRFO: response?.numberOfFilesWFCRFO || missingField,
    numberOfFilesPending: response?.numberOfFilesPending || missingField,
    verifiedDate: response?.verifiedDate ? moment(response.verifiedDate).format('MM/DD/YYYY') : missingField,
  } as IReportSectionProps;
};

export const flattenrfoDetails = (response: IOrderReportDetails) => {
  return {
    earlyArrivalDay:
      response?.earlyArrivalDay && response?.earlyArrivalNotes
        ? response?.earlyArrivalDay + ' - ' + response.earlyArrivalNotes
        : missingField,
    jobDescription: response?.jobDescription || missingField,
    notes: response?.notes || missingField,
    requiredQualifications: response?.requiredQualifications || missingField,
    preferredQualifications: response?.preferredQualifications || missingField,
  } as IRfoSectionProps;
};

export const flattenLTRestrictionDetails = (response: IOrderReportDetails) => {
  const facilityBRShiftData = response?.facilityBRShiftDifferentials
    ?.slice()
    .sort((a, b) => (b.skillset < a.skillset ? -1 : 1))
    .map(item => {
      return {
        skillset: item.skillset || missingField,
        contractID: item.contractID || missingField,
        billRateID: item.billRateID || missingField,
        startTime: item.startTime ? formatTime(item.startTime) : missingField,
        endTime: item.endTime ? formatTime(item.endTime) : missingField,
        amount: item.amount ? formatNumberWithCommas(item.amount) : missingField,
        shiftDifferentialTypeID: item.shiftDifferentialTypeID || missingField,
        shiftDifferentialType: item.shiftDifferentialType || missingField,
        addedByName: item.addedByName || missingField,
        shiftDifferentialDescType: item.shiftDifferentialDescType || missingField,
        templateTypeID: item.templateTypeID || missingField,
      };
    });

  const facilityBillrateData = response?.facilityBillRates?.slice().map(item => {
    return {
      skillset: item.skillset || missingField,
      billRateId: item.billRateId || missingField,
      contractId: item.contractId || missingField,
      descShift: item.descShift || missingField,
      regularRate: item.regularRate ? formatNumberWithCommas(item.regularRate) : missingField,
      overtimeFactor: item.overtimeFactor ? Number(item.overtimeFactor).toFixed(2) : missingField,
      overtimeRate: item.overtimeRate ? formatNumberWithCommas(item.overtimeRate) : missingField,
      doubletimeFactor: item.doubletimeFactor ? Number(item.doubletimeFactor).toFixed(2) : missingField,
      doubleTimeRate: item.doubleTimeRate ? formatNumberWithCommas(item.doubleTimeRate) : missingField,
      holidayRate: item.holidayRate ? formatNumberWithCommas(item.holidayRate) : missingField,
      onCallRate: item.onCallRate ? formatNumberWithCommas(item.onCallRate) : missingField,
      callBackFactor: item.callBackFactor ? Number(item.callBackFactor).toFixed(2) : missingField,
      callBackRate: item.callBackRate ? formatNumberWithCommas(item.callBackRate) : missingField,
      chargePremium: item.chargePremium ? formatNumberWithCommas(item.chargePremium) : missingField,
      orientationRate: item.orientationRate ? formatNumberWithCommas(item.orientationRate) : missingField,
      orientationFactor: item.orientationFactor ? Number(item.orientationFactor).toFixed(2) : missingField,
      descHousingProvider: item.descHousingProvider || missingField,
      notes: item.notes || missingField,
      overtimeTotal: item.overtimeTotal || missingField,
      doubletimeTotal: item.doubletimeTotal || missingField,
      holidayTotal: item.holidayTotal ? formatNumberWithCommas(item.holidayTotal) : missingField,
      callbackTotal: item.callbackTotal ? Number(item.callbackTotal).toFixed(2) : missingField,
    };
  });
  return {
    radiusRule: response?.radiusRule || missingField,
    permAddressWithIn: response?.permAddressWithIn || missingField,
    billRateAgreement: response?.billRateAgreement || missingField,
    benefitResult: response?.benefitResult || missingField,
    restrictionNotes: response?.restrictionNotes || missingField,
    facilityBillrate:
      response?.facilityBillRates?.length > 0
        ? facilityBillrateData
        : [
            {
              skillset: missingField,
              billRateId: missingField,
              contractId: missingField,
              descShift: missingField,
              regularRate: missingField,
              overtimeFactor: missingField,
              overtimeRate: missingField,
              doubletimeFactor: missingField,
              doubleTimeRate: missingField,
              holidayRate: missingField,
              onCallRate: missingField,
              callBackFactor: missingField,
              callBackRate: missingField,
              chargePremium: missingField,
              orientationRate: missingField,
              orientationFactor: missingField,
              descHousingProvider: missingField,
              notes: missingField,
              overtimeTotal: missingField,
              doubletimeTotal: missingField,
              holidayTotal: missingField,
              callbackTotal: missingField,
            },
          ],
    facilityBRShiftDifferentials:
      response?.facilityBRShiftDifferentials?.length > 0
        ? facilityBRShiftData
        : [
            {
              skillset: missingField,
              contractID: missingField,
              billRateID: missingField,
              startTime: missingField,
              endTime: missingField,
              amount: missingField,
              shiftDifferentialTypeID: missingField,
              shiftDifferentialType: missingField,
              addedByName: missingField,
              shiftDifferentialDescType: missingField,
              templateTypeID: missingField,
            },
          ],
    amnDivisionTypeId: response?.amnDivisionTypeId || missingField,
  } as ILTRestrictionSectionProps;
};

export const flattenFacilityDetails = (response: IOrderReportDetails) => {
  return {
    doNotCallFacility: response?.doNotCallFacility ? (response?.doNotCallFacility == 'Y' ? 'Yes' : 'No') : missingField,
    postponementPenaltyAmt: response?.postponementPenaltyAmt
      ? formatNumberWithCommas(response?.postponementPenaltyAmt)
      : missingField,
    facilityType:
      response?.facilityType && response?.facilityDesc && response?.facilitySettings
        ? response?.facilityType + ',' + response?.facilityDesc + ',' + response?.facilitySettings
        : missingField,
    postPonementPenaltyFreq: response?.postPonementPenaltyFreq || missingField,
    facilityCity: response?.facilityCity || missingField,
    cancellationPenaltyAmt: response?.cancellationPenaltyAmt
      ? formatNumberWithCommas(response?.cancellationPenaltyAmt)
      : missingField,
    website: response?.website || missingField,
    cancellationPenaltyFreq: response?.cancellationPenaltyFreq || missingField,
    currentAssignments: response?.currentAssignments || missingField,
    numberOfBeds: response?.numberOfBeds || missingField,
    facilityPreferenceNotes: response?.facilityPreferenceNotes || missingField,
    facilityName:
      response?.facilityName && response?.facilityId && response?.affiliate
        ? response?.facilityName + ' (' + response?.facilityId + ') ' + response?.affiliate
        : missingField,
  } as IFacilitySectionProps;
};

export const flattenMealDetails = (response: IOrderReportDetails) => {
  return {
    mealsAndIncidentals:
      response?.mealsAndIncidentals && response?.mealsAndIncidentalsFrequency
        ? formatNumberWithCommas(response?.mealsAndIncidentals) + '  ' + response?.mealsAndIncidentalsFrequency
        : missingField,
  } as IMealSectionProps;
};

export const flattenPayBonusDetails = (response: IOrderReportDetails) => {
  const orderPayrateData = response?.orderPayRates
    ?.slice()
    .sort((a, b) => (b.skillset > a.skillset ? -1 : 1))
    .map(item => {
      return {
        skillset: item.skillset || missingField,
        facilityId: item.facilityId || missingField,
        facilityPPId: item.facilityPPId || missingField,
        payRateId: item.payRateId || missingField,
        payRateLineId: item.payRateLineId || missingField,
        shiftId: item.shiftId || missingField,
        regularRate: item.regularRate ? formatNumberWithCommas(item.regularRate) : missingField,
        overtimeFactor: item.overtimeFactor ? Number(item.overtimeFactor).toFixed(2) : missingField,
        doubletimeFactor: item.doubletimeFactor ? Number(item.doubletimeFactor).toFixed(2) : missingField,
        holidayFactor: item.holidayFactor ? formatNumberWithCommas(item.holidayFactor) : missingField,
        onCallRate: item.onCallRate ? formatNumberWithCommas(item.onCallRate) : missingField,
        callBackFactor: item.callBackFactor || missingField,
        chargePremium: item.chargePremium ? formatNumberWithCommas(item.chargePremium) : missingField,
        timestamp: item.timestamp || missingField,
        holidayRate: item.holidayRate ? formatNumberWithCommas(item.holidayRate) : missingField,
        callbackRate: item.callbackRate ? formatNumberWithCommas(item.callbackRate) : missingField,
        payroll: item.payroll || missingField,
        paysGWW: item.paysGWW || missingField,
        descShift: item.descShift || missingField,
        compositeRate: item.compositeRate ? formatNumberWithCommas(item.compositeRate) : missingField,
        takeHomeRate: item.takeHomeRate ? formatNumberWithCommas(item.takeHomeRate) : missingField,
        additionalPremiumPay: item.additionalPremiumPay ? Number(item.additionalPremiumPay).toFixed(2) : missingField,
      };
    });

  const facilityPPShiftData = response?.facilityPPShiftDifferentials
    ?.slice()
    .sort((a, b) => (b.skillset > a.skillset ? -1 : 1))
    .map(item => {
      return {
        skillset: item.skillset || missingField,
        facilityId: item.facilityId || missingField,
        facilityPPId: item.facilityPPId || missingField,
        payRateId: item.payRateId || missingField,
        startTime: item.startTime ? formatTime(item.startTime) : missingField,
        endTime: item.endTime ? formatTime(item.endTime) : missingField,
        amount: item.amount ? formatNumberWithCommas(item.amount) : missingField,
        shiftDifferentialTypeId: item.shiftDifferentialTypeId || missingField,
        descShiftDifferentialType: item.descShiftDifferentialType || missingField,
        addedByName: item.addedByName || missingField,
        descShiftDifferentialDescriptionType: item.descShiftDifferentialDescriptionType || missingField,
        templateTypeId: item.templateTypeId || missingField,
      };
    });

  const facilityPpPayCyleData = response?.facilityPpPayCyle?.slice().map(item => {
    return {
      facilityId: item.facilityId || missingField,
      facilityPPId: item.facilityPPId || missingField,
      payCycleId: item.payCycleId || missingField,
      shiftTypeId: item.shiftTypeId || missingField,
      payCycleTypeId: item.payCycleTypeId || missingField,
      startDay: item.startDay ? item.startDay : missingField,
      endDay: item.endDay || missingField,
      startTime: item.startTime ? item.startTime : missingField,
      endTime: item.endTime ? item.endTime : missingField,
      descShiftType: item.descShiftType || missingField,
      descPayCycleType: item.descPayCycleType || missingField,
      descStartDay:
        item.descStartDay && item.startTime ? item.descStartDay + ' at ' + formatTime(item.startTime) : missingField,
      descEndDay: item.descEndDay && item.endTime ? item.descEndDay + ' at ' + formatTime(item.endTime) : missingField,
    };
  });

  const guaranteedWorkWeeksData = response?.guaranteedWorkWeeks?.slice().map(item => {
    return {
      contractID: item.contractID || missingField,
      gwwid: item.gwwid || missingField,
      shiftID: item.shiftID || missingField,
      hoursPerPayCycle: item.hoursPerPayCycle ? Number(item.hoursPerPayCycle).toString() : missingField,
      timestamp: item.timestamp || missingField,
      cancellationHoursAllowed: item.cancellationHoursAllowed
        ? Number(item.cancellationHoursAllowed).toString()
        : missingField,
      cancellationHoursFrequencyID: item.cancellationHoursFrequencyID || missingField,
      frequency: item.frequency || missingField,
      gwwException: item.gwwException || missingField,
      gwwExceptionNotes: item.gwwExceptionNotes || missingField,
    };
  });

  const orderBonusData = response?.orderBonus?.slice().map(item => {
    return {
      orderID: item.orderID || missingField,
      bonusID: item.bonusID || missingField,
      bonusAmount: item.bonusAmount ? formatNumberWithCommas(item.bonusAmount) : missingField,
      shifts: item.shifts || missingField,
      billingAmount: item.billingAmount ? formatNumberWithCommas(item.billingAmount) : missingField,
      approvalDate: item.approvalDate || missingField,
      startdate: item.startdate ? moment(item.startdate).format('MM/DD/YYYY') : missingField,
      endDate: item.endDate ? moment(item.endDate).format('MM/DD/YYYY') : missingField,
      minimumWeeksWorked: item.minimumWeeksWorked || missingField,
      descSortAssignment: item.descSortAssignment || missingField,
      descBonusType: item.descBonusType || missingField,
      descPayer: item.descPayer || missingField,
      descBillingType: item.descBillingType || missingField,
      descPaymentType: item.descPaymentType || missingField,
      descApprovalType: item.descApprovalType || missingField,
      descShortHoursType: item.descShortHoursType || missingField,
      notes: item.notes || missingField,
    };
  });
  return {
    payRates:
      response?.orderPayRates?.length > 0
        ? orderPayrateData
        : [
            {
              skillset: missingField,
              facilityId: missingField,
              facilityPPId: missingField,
              payRateId: missingField,
              payRateLineId: missingField,
              shiftId: missingField,
              regularRate: missingField,
              overtimeFactor: missingField,
              doubletimeFactor: missingField,
              holidayFactor: missingField,
              onCallRate: missingField,
              callBackFactor: missingField,
              chargePremium: missingField,
              timestamp: missingField,
              holidayRate: missingField,
              callbackRate: missingField,
              payroll: missingField,
              paysGWW: missingField,
              descShift: missingField,
              compositeRate: missingField,
              takeHomeRate: missingField,
              additionalPremiumPay: missingField,
            },
          ],
    pPShiftDifferentials:
      response?.facilityPPShiftDifferentials?.length > 0
        ? facilityPPShiftData
        : [
            {
              skillset: missingField,
              facilityId: missingField,
              facilityPPId: missingField,
              payRateId: missingField,
              startTime: missingField,
              endTime: missingField,
              amount: missingField,
              shiftDifferentialTypeId: missingField,
              descShiftDifferentialType: missingField,
              addedByName: missingField,
              descShiftDifferentialDescriptionType: missingField,
              templateTypeId: missingField,
            },
          ],
    facilityPpPayCyle:
      response?.facilityPpPayCyle?.length > 0
        ? facilityPpPayCyleData
        : [
            {
              facilityId: missingField,
              facilityPPId: missingField,
              payCycleId: missingField,
              shiftTypeId: missingField,
              payCycleTypeId: missingField,
              startDay: missingField,
              endDay: missingField,
              startTime: missingField,
              endTime: missingField,
              descShiftType: missingField,
              descPayCycleType: missingField,
              descStartDay: missingField,
              descEndDay: missingField,
            },
          ],
    guaranteedWorkWeeks:
      response?.guaranteedWorkWeeks?.length > 0
        ? guaranteedWorkWeeksData
        : [
            {
              contractID: missingField,
              gwwid: missingField,
              shiftID: missingField,
              hoursPerPayCycle: missingField,
              timestamp: missingField,
              cancellationHoursAllowed: missingField,
              cancellationHoursFrequencyID: missingField,
              frequency: missingField,
              gwwException: missingField,
              gwwExceptionNotes: missingField,
            },
          ],
    orderBonus:
      response?.orderBonus?.length > 0
        ? orderBonusData
        : [
            {
              orderID: missingField,
              bonusID: missingField,
              bonusAmount: missingField,
              shifts: missingField,
              billingAmount: missingField,
              approvalDate: missingField,
              startdate: missingField,
              endDate: missingField,
              minimumWeeksWorked: missingField,
              descSortAssignment: missingField,
              descBonusType: missingField,
              descPayer: missingField,
              descBillingType: missingField,
              descPaymentType: missingField,
              descApprovalType: missingField,
              descShortHoursType: missingField,
              notes: missingField,
            },
          ],
    descTimecardType: response?.descTimecardType || missingField,
    descTimeSubmittedBY: response?.descTimeSubmittedBY || missingField,
    carRental: response?.carRental || missingField,
    notes: response?.notes || missingField,
    amnDivisionTypeId: response?.amnDivisionTypeId || missingField,
    facilityPPGWWExceptions: response?.facilityPPGWWExceptions || missingField,
  } as IPayBonusSectionProps;
};

export const flattenHousingDetails = (response: IOrderReportDetails) => {
  const orderHousingData = response?.orderHousings
    ?.slice()
    .sort((a, b) => (b.descHousingProvider < a.descHousingProvider ? -1 : 1))
    .map(item => {
      return {
        amount: item.amount ? formatNumberWithCommas(item.amount) : missingField,
        descHousing: item.descHousing || missingField,
        housingProviderId: item.housingProviderId || missingField,
        descHousingProvider: item.descHousingProvider || missingField,
        utilityCharge: item.utilityCharge ? formatNumberWithCommas(item.utilityCharge) : missingField,
      };
    });
  return {
    housingType: response?.housingType || missingField,
    limitation: response?.limitation || missingField,
    housingDescription: response?.housingDescription || missingField,
    orderHousings:
      response?.orderHousings?.length > 0
        ? orderHousingData
        : [
            {
              housingDescriptionId: missingField,
              amount: missingField,
              descHousing: missingField,
              housingProviderId: missingField,
              descHousingProvider: missingField,
              utilityCharge: missingField,
            },
          ],
  } as IHousingSectionProps;
};

export const flattenUnitDetails = (response: IOrderReportDetails) => {
  return {
    unitSize: response?.unitSize || missingField,
    nurseToPatientRatio: response?.nurseToPatientRatio || missingField,
    typicalDiagnoses: response?.typicalDiagnoses || missingField,
    commonMedications: response?.commonMedications || missingField,
    specialSkills: response?.specialSkills || missingField,
    useOfAssistivePersonnel: response?.useOfAssistivePersonnel || missingField,
    computerSkills: response?.computerSkills || missingField,
    sellingPoints: response?.sellingPoints || missingField,
    other: response?.other || missingField,
    unitName: response?.unitName || missingField,
  } as IUnitSectionProps;
};

export const flattenTravelDetails = (response: IOrderReportDetails) => {
  const travelAllownaveData = response?.travelAllowances?.map(item => {
    return {
      descAllowance: item.descAllowance || missingField,
      amountPerMile: item.amountPerMile ? formatAmount(item.amountPerMile) : missingField,
      allowanceCap: item.allowanceCap ? formatAmount(item.allowanceCap) : missingField,
      descPayer: item.descPayer || missingField,
    };
  });
  return {
    travelAllowances:
      response?.travelAllowances?.length > 0
        ? travelAllownaveData
        : [
            {
              descAllowance: missingField,
              descPayer: missingField,
              amountPerMile: missingField,
              allowanceCap: missingField,
            },
          ],
    onTheJobMileage: response?.onTheJobMileage ? formatNumberWithCommas(response?.onTheJobMileage) : missingField,
    descJobMileagePayer: response?.descJobMileagePayer || missingField,
  } as ITravelSectionProps;
};

export const flattenPointOfIntrests = (response: IOrderReportDetails) => {
  return {
    demographics: response?.demographics || missingField,
    facilityHighlights: response?.facilityHighlights || missingField,
  } as IPointOfIntrestSectionProps;
};
